import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";

function Stat({ label, value }) {
  return (
    <div className="bg-white rounded-[18px] p-5 shadow-sm">
      <div className="text-[11px] uppercase tracking-widest text-[#6b6894] font-semibold">{label}</div>
      <div className="mt-1 text-3xl font-display font-semibold text-[#1a1640] tabular-nums">
        {Number(value ?? 0).toLocaleString("en-IN")}
      </div>
    </div>
  );
}

export default function Analytics() {
  const [params, setParams] = useSearchParams();
  const token = params.get("token") || "";
  const [input, setInput] = useState(token);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  async function fetchData(tk) {
    setErr("");
    try {
      const { data } = await api.get("/admin/analytics", { params: { token: tk } });
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Unauthorized");
      setData(null);
    }
  }

  useEffect(() => {
    if (token) fetchData(token);
    const id = setInterval(() => token && fetchData(token), 30000);
    return () => clearInterval(id);
  }, [token]);

  const maxDay = Math.max(1, ...(data?.by_day?.map((d) => d.views) || [1]));

  return (
    <div className="App">
      <Header compact />

      <main className="max-w-4xl mx-auto px-6 pt-8 pb-24">
        <h1 className="font-display text-3xl sm:text-4xl font-semibold text-[#1a1640]" data-testid="analytics-title">
          Site Analytics
        </h1>
        <p className="mt-2 text-[#6b6894] text-[15px]">
          Private dashboard · auto-refreshes every 30s · works even when Google Analytics is blocked.
        </p>

        {!data && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setParams({ token: input });
            }}
            className="mt-6 flex flex-wrap gap-2 bg-white rounded-[18px] p-4 shadow-sm"
          >
            <input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="admin token"
              className="flex-1 min-w-[220px] rounded-xl border border-slate-200 bg-[#faf7ea] px-3 py-2 text-[14px] outline-none focus:border-indigo-400"
              data-testid="analytics-token-input"
            />
            <button type="submit" className="myneta-btn" style={{ padding: "10px 16px", fontSize: 13 }} data-testid="analytics-load-btn">
              Load
            </button>
          </form>
        )}

        {err && (
          <div className="mt-4 text-rose-600 font-semibold text-sm" data-testid="analytics-error">
            {err}
          </div>
        )}

        {data && (
          <>
            {/* Totals */}
            <section className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="analytics-totals">
              <Stat label="Page views — all time" value={data.totals?.page_views} />
              <Stat label="Unique visitors — all time" value={data.totals?.unique_visitors} />
              <Stat label="Page views · 24h" value={data.last_24h?.page_views} />
              <Stat label="Unique visitors · 24h" value={data.last_24h?.unique_visitors} />
              <Stat label="Page views · 7d" value={data.last_7d?.page_views} />
              <Stat label="Unique visitors · 7d" value={data.last_7d?.unique_visitors} />
              <Stat label="Page views · 30d" value={data.last_30d?.page_views} />
              <Stat label="Unique visitors · 30d" value={data.last_30d?.unique_visitors} />
            </section>

            {/* By day bar chart (CSS bars) */}
            {data.by_day?.length > 0 && (
              <section className="mt-6 bg-white rounded-[18px] p-5 shadow-sm" data-testid="analytics-by-day">
                <div className="text-[11px] uppercase tracking-widest text-[#6b6894] font-semibold mb-3">
                  Last 14 days · views per day
                </div>
                <div className="flex items-end gap-1.5 h-40">
                  {data.by_day.map((d) => (
                    <div key={d.day} className="flex-1 flex flex-col items-center gap-1">
                      <div
                        className="w-full rounded-t bg-indigo-500"
                        style={{ height: `${(d.views / maxDay) * 100}%`, minHeight: 2 }}
                        title={`${d.day}: ${d.views} views · ${d.uniq} unique`}
                      />
                      <div className="text-[10px] text-[#6b6894] truncate">{d.day.slice(5)}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Top paths */}
            {data.top_paths?.length > 0 && (
              <section className="mt-6 bg-white rounded-[18px] p-5 shadow-sm" data-testid="analytics-top-paths">
                <div className="text-[11px] uppercase tracking-widest text-[#6b6894] font-semibold mb-3">
                  Top pages · last 30 days
                </div>
                <div className="divide-y divide-slate-100">
                  {data.top_paths.map((p) => (
                    <div key={p.path} className="flex items-baseline gap-3 py-2 text-sm">
                      <code className="flex-1 truncate text-[13px] text-[#1a1640]">{p.path}</code>
                      <span className="tabular-nums font-semibold text-[#1a1640]">{p.views}</span>
                      <span className="tabular-nums text-xs text-[#6b6894]">{p.uniq} uniq</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Language split */}
            {data.lang_split?.length > 0 && (
              <section className="mt-6 bg-white rounded-[18px] p-5 shadow-sm">
                <div className="text-[11px] uppercase tracking-widest text-[#6b6894] font-semibold mb-3">
                  Language · last 7 days
                </div>
                <div className="flex gap-4 text-sm">
                  {data.lang_split.map((l) => (
                    <div key={l._id} className="bg-[#f3eedc] rounded-xl px-4 py-2">
                      <span className="font-semibold text-[#1a1640]">{l._id === "ta" ? "தமிழ்" : "English"}</span>{" "}
                      <span className="tabular-nums">· {l.views}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <div className="mt-6 text-xs text-[#8c89ad]">
              Updated {new Date(data.generated_at).toLocaleString()}
            </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
